import { DEFAULT_CONFIG, MAX_PLAYERS, type ConfigResult, type GameConfig, validateConfig } from './config';

/** Modes understood by the room protocol and persisted room records. */
export const GAME_MODES = ['versus', 'daily', 'casual', 'competitive', 'endless', 'practice'] as const;
export type GameMode = (typeof GAME_MODES)[number];

/** Public best-score leaderboard scopes. Competitive standings are the rank ladder instead. */
const LEADERBOARD_BOARDS = ['today', 'all-time'] as const;
export type LeaderboardBoard = (typeof LEADERBOARD_BOARDS)[number];

/** Endless reads `roundDuration` as the board time: how long each board lasts once play on it begins. */
const ENDLESS_BOARD_SECONDS = 60;

const SOLO_TIMED_CONFIG: GameConfig = {
  ...DEFAULT_CONFIG,
  maxPlayers: 1,
  scoringMode: 'shared',
};

/** Ranked 1v1: the standard 4×4, 90-second, 3+ format with Classic scoring (unique words 1.5×). */
const RANKED_CONFIG: GameConfig = {
  ...DEFAULT_CONFIG,
  maxPlayers: 2,
  scoringMode: 'classic',
};

/**
 * Defaults for each mode. Versus remains configurable except for capacity; every other
 * mode starts from its entry here and changes only its `SOLO_SETUP_FIELDS`.
 */
export const MODE_CONFIG = {
  versus: { ...DEFAULT_CONFIG },
  daily: { ...SOLO_TIMED_CONFIG },
  casual: { ...SOLO_TIMED_CONFIG },
  competitive: { ...RANKED_CONFIG },
  endless: { ...SOLO_TIMED_CONFIG, roundDuration: ENDLESS_BOARD_SECONDS },
  practice: { ...SOLO_TIMED_CONFIG },
} as const satisfies Record<GameMode, GameConfig>;

/** Settings a solo player may change before starting; every other solo setting stays locked. */
const SOLO_SETUP_FIELDS: Partial<Record<GameMode, readonly (keyof GameConfig)[]>> = {
  casual: ['boardSize', 'difficulty', 'roundDuration', 'minWordLength'],
  endless: ['boardSize', 'difficulty', 'roundDuration', 'minWordLength'],
  practice: ['difficulty'],
};

export function soloSetupFields(mode: GameMode): readonly (keyof GameConfig)[] {
  return SOLO_SETUP_FIELDS[mode] ?? [];
}

export function isGameMode(value: unknown): value is GameMode {
  return typeof value === 'string' && GAME_MODES.includes(value as GameMode);
}

export function isLeaderboardBoard(value: unknown): value is LeaderboardBoard {
  return typeof value === 'string' && LEADERBOARD_BOARDS.includes(value as LeaderboardBoard);
}

export const SOLO_MODES = ['casual', 'endless', 'practice'] as const;
/** Modes listed on the Solo menu; Daily and Competitive sit on the main menu. */
export function isSoloMode(mode: GameMode): mode is (typeof SOLO_MODES)[number] {
  return (SOLO_MODES as readonly GameMode[]).includes(mode);
}

export function isUntimedMode(mode: GameMode): boolean {
  return mode === 'endless' || mode === 'practice';
}

/**
 * Endless rounds never end on a clock: the board is replaced `roundDuration` seconds after the
 * player's first touch on it, and the round runs until the player finishes.
 */
export function rotatesBoards(mode: GameMode): boolean {
  return mode === 'endless';
}

/** Return a fresh mode default so callers cannot mutate MODE_CONFIG. */
export function modeConfig(mode: GameMode): GameConfig {
  return { ...MODE_CONFIG[mode] };
}

/**
 * Validate a room configuration. Locked modes always resolve to their fixed
 * server configuration, regardless of client input; Versus capacity is likewise
 * server-owned. Solo setup fields are changed after creation through the room's
 * config message, never at creation.
 */
export function configForMode(mode: GameMode, input: unknown): ConfigResult {
  if (mode !== 'versus') return { ok: true, value: modeConfig(mode) };
  const result = validateConfig(input, DEFAULT_CONFIG);
  if (!result.ok) return result;
  return { ok: true, value: { ...result.value, maxPlayers: MAX_PLAYERS } };
}
